import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { leadsApi } from "@/api/leads";
import { useAuth } from "@/contexts/AuthContext";
import { isCrmViewOnlyUser } from "@/utils/crmAccess";
import { toast } from "sonner";
import { ChevronDown, ChevronRight } from "lucide-react";
import {
  LeadDetailCard,
  LeadDetailFieldLabel,
  LD_SELECT_TRIGGER,
} from "@/components/crm/leadDetailArtisUi";

const AUTHORITY_FALLBACK: Record<string, string> = {
  "1": "Needs to ask parents",
  "2": "Need to ask family",
  "3": "Need to ask husband/wife",
  "4": "Need to ask someone",
  "5": "Can make the decision themself",
};
const NEED_FALLBACK: Record<string, string> = {
  "1": "No Need (was just checking)",
  "2": "Has little need",
  "3": "Has somewhat of a need",
  "4": "Lot of need",
  "5": "Desperate need",
};
const TIMELINE_FALLBACK: Record<string, string> = {
  "1": "Not Sure",
  "2": "In a few months",
  "3": "Within this month",
  "4": "Within this week",
  "5": "Immediately",
};

function tierBadgeClass(tier: string | null | undefined): string {
  switch (tier) {
    case "Hot":
      return "bg-red-100 text-red-800 border-red-200";
    case "Warm":
      return "bg-orange-100 text-orange-800 border-orange-200";
    case "Cold":
      return "bg-sky-100 text-sky-800 border-sky-200";
    case "Bad":
      return "bg-slate-100 text-slate-700 border-slate-200";
    default:
      return "bg-slate-50 text-slate-500 border-slate-200";
  }
}

type Props = {
  leadId: string;
  aestheticProcedureName?: string | null;
  procedureInterest?: string | null;
};

export function LeadBantScoringCard({
  leadId,
  aestheticProcedureName,
  procedureInterest,
}: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const viewOnly = isCrmViewOnlyUser(user);
  const canEdit =
    !viewOnly &&
    (user?.role === "SALES_PERSON" ||
      user?.role === "SALES_MANAGER" ||
      user?.role === "HOSPITAL_ADMIN");

  const [historyOpen, setHistoryOpen] = useState(false);
  const [draft, setDraft] = useState<{
    budgetScore: string;
    authorityScore: string;
    needScore: string;
    timelineScore: string;
  } | null>(null);

  const q = useQuery({
    queryKey: ["lead-bant-score", leadId],
    queryFn: async () => (await leadsApi.getLeadBantScore(leadId)).data.data,
    enabled: !!leadId,
  });

  const data = q.data;
  const procedure =
    data?.procedure ||
    aestheticProcedureName?.trim() ||
    procedureInterest?.trim() ||
    null;

  const budgetBands: Record<string, string> | null = useMemo(() => {
    const bands = data?.meta?.budgetBandsByProcedure?.[procedure as string];
    return bands || null;
  }, [data?.meta, procedure]);

  const showBudget = !!budgetBands;

  const values = draft ?? {
    budgetScore: data?.budgetScore != null ? String(data.budgetScore) : "",
    authorityScore: data?.authorityScore != null ? String(data.authorityScore) : "",
    needScore: data?.needScore != null ? String(data.needScore) : "",
    timelineScore: data?.timelineScore != null ? String(data.timelineScore) : "",
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      const toScore = (s: string) => (s ? Number(s) : null);
      const budgetScore = showBudget ? toScore(values.budgetScore) : null;
      return leadsApi.upsertLeadBantScore(leadId, {
        budgetScore,
        authorityScore: toScore(values.authorityScore),
        needScore: toScore(values.needScore),
        timelineScore: toScore(values.timelineScore),
        budgetWasWontReveal: budgetScore === 3,
      });
    },
    onSuccess: () => {
      toast.success("Lead scoring saved");
      setDraft(null);
      queryClient.invalidateQueries({ queryKey: ["lead-bant-score", leadId] });
      queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      toast.error(err?.response?.data?.message || "Failed to save BANT score");
    },
  });

  const setField = (key: keyof typeof values, v: string) => {
    setDraft((prev) => ({
      budgetScore: prev?.budgetScore ?? values.budgetScore,
      authorityScore: prev?.authorityScore ?? values.authorityScore,
      needScore: prev?.needScore ?? values.needScore,
      timelineScore: prev?.timelineScore ?? values.timelineScore,
      [key]: v === "__clear__" ? "" : v,
    }));
  };

  const authorityLabels = data?.meta?.authorityLabels || AUTHORITY_FALLBACK;
  const needLabels = data?.meta?.needLabels || NEED_FALLBACK;
  const timelineLabels = data?.meta?.timelineLabels || TIMELINE_FALLBACK;

  return (
    <LeadDetailCard title="Lead scoring (BANT)">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <p className="ld-muted text-xs">
          BANT profile (Budget / Authority / Need / Timeline). Does not block CRM stage changes.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {data?.weightedScore != null && (
            <span className="text-xs font-medium tabular-nums text-[var(--ld-ink)]">
              Score: {Number(data.weightedScore).toFixed(2)}
            </span>
          )}
          <span
            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${tierBadgeClass(
              data?.tier
            )}`}
          >
            {data?.tier || "Unscored"}
          </span>
          {!data?.profileComplete && (
            <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-900">
              Profile Incomplete
            </span>
          )}
        </div>
      </div>

      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading scoring…</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {showBudget ? (
            <div className="space-y-1">
              <LeadDetailFieldLabel>Budget (B)</LeadDetailFieldLabel>
              <Select
                value={values.budgetScore || "__clear__"}
                onValueChange={(v) => setField("budgetScore", v)}
                disabled={!canEdit}
              >
                <SelectTrigger className={LD_SELECT_TRIGGER}>
                  <SelectValue placeholder="Select budget band" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__clear__">Not set</SelectItem>
                  {([1, 2, 3, 4, 5] as const).map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n} — {budgetBands![String(n)] || budgetBands![n]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-slate-200 bg-white/60 p-3 text-xs text-slate-500 md:col-span-2">
              Budget scoring is hidden until a supported aesthetic procedure is set on the lead
              (Rhinoplasty, Liposuction, Tummy Tuck, Breast Reduction, or Gynecomastia).
            </div>
          )}

          <div className="space-y-1">
            <LeadDetailFieldLabel>Authority (A)</LeadDetailFieldLabel>
            <Select
              value={values.authorityScore || "__clear__"}
              onValueChange={(v) => setField("authorityScore", v)}
              disabled={!canEdit}
            >
              <SelectTrigger className={LD_SELECT_TRIGGER}>
                <SelectValue placeholder="Select authority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__clear__">Not set</SelectItem>
                {([1, 2, 3, 4, 5] as const).map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n} — {authorityLabels[String(n)] || AUTHORITY_FALLBACK[String(n)]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <LeadDetailFieldLabel>Need (N)</LeadDetailFieldLabel>
            <Select
              value={values.needScore || "__clear__"}
              onValueChange={(v) => setField("needScore", v)}
              disabled={!canEdit}
            >
              <SelectTrigger className={LD_SELECT_TRIGGER}>
                <SelectValue placeholder="Select need" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__clear__">Not set</SelectItem>
                {([1, 2, 3, 4, 5] as const).map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n} — {needLabels[String(n)] || NEED_FALLBACK[String(n)]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <LeadDetailFieldLabel>Timeline (T)</LeadDetailFieldLabel>
            <Select
              value={values.timelineScore || "__clear__"}
              onValueChange={(v) => setField("timelineScore", v)}
              disabled={!canEdit}
            >
              <SelectTrigger className={LD_SELECT_TRIGGER}>
                <SelectValue placeholder="Select timeline" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__clear__">Not set</SelectItem>
                {([1, 2, 3, 4, 5] as const).map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n} — {timelineLabels[String(n)] || TIMELINE_FALLBACK[String(n)]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {canEdit && (
        <div className="flex justify-end gap-2">
          {draft && (
            <Button type="button" variant="ghost" size="sm" onClick={() => setDraft(null)}>
              Reset
            </Button>
          )}
          <button
            type="button"
            className="ld-btn-primary ld-motion w-auto px-5"
            disabled={saveMut.isPending || !draft}
            onClick={() => saveMut.mutate()}
          >
            {saveMut.isPending ? "Saving…" : "Save scoring"}
          </button>
        </div>
      )}

      {(data?.history?.length ?? 0) > 0 && (
        <div className="border-t border-[var(--ld-line)] pt-3 mt-3">
          <button
            type="button"
            className="flex items-center gap-1 text-xs font-medium text-[var(--ld-ink-soft)]"
            onClick={() => setHistoryOpen((o) => !o)}
          >
            {historyOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            Score history ({data.history.length})
          </button>
          {historyOpen && (
            <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border bg-white text-xs">
              <table className="min-w-full">
                <thead className="bg-slate-50 text-muted-foreground">
                  <tr>
                    <th className="p-2 text-left">When</th>
                    <th className="p-2 text-left">By</th>
                    <th className="p-2 text-center">B</th>
                    <th className="p-2 text-center">A</th>
                    <th className="p-2 text-center">N</th>
                    <th className="p-2 text-center">T</th>
                    <th className="p-2 text-right">Score</th>
                    <th className="p-2 text-left">Tier</th>
                  </tr>
                </thead>
                <tbody>
                  {data.history.map((h: any, idx: number) => (
                    <tr key={`${h.updatedAt}-${idx}`} className="border-t">
                      <td className="p-2 whitespace-nowrap">
                        {h.updatedAt
                          ? new Date(h.updatedAt).toLocaleString("en-IN", {
                              timeZone: "Asia/Kolkata",
                            })
                          : "—"}
                      </td>
                      <td className="p-2">{h.updatedBy?.name || "—"}</td>
                      <td className="p-2 text-center">{h.budgetScore ?? "—"}</td>
                      <td className="p-2 text-center">{h.authorityScore ?? "—"}</td>
                      <td className="p-2 text-center">{h.needScore ?? "—"}</td>
                      <td className="p-2 text-center">{h.timelineScore ?? "—"}</td>
                      <td className="p-2 text-right tabular-nums">
                        {h.weightedScore != null ? Number(h.weightedScore).toFixed(2) : "—"}
                      </td>
                      <td className="p-2">{h.tier || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </LeadDetailCard>
  );
}
